import React, { useState } from 'react';
import { 
    SchoolTheme, 
    SectionVisibility, 
    NavItem,
    DEFAULT_THEME, 
    DEFAULT_SECTION_VISIBILITY,
    FONT_OPTIONS,
    COLOR_PRESETS 
} from '../Ownerfiles/website-designs/types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { 
    Palette, 
    Type, 
    LayoutGrid, 
    Eye, 
    EyeOff, 
    Navigation, 
    ChevronDown, 
    ChevronUp,
    GripVertical,
    Plus,
    Trash2,
    Check,
    Undo2
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ThemeEditorProps {
    theme: SchoolTheme;
    sectionVisibility: SectionVisibility;
    navigation: NavItem[];
    onThemeChange: (theme: SchoolTheme) => void;
    onSectionVisibilityChange: (visibility: SectionVisibility) => void;
    onNavigationChange: (nav: NavItem[]) => void;
    onUpdateField?: (field: string, value: any) => void;
}

export const ThemeEditor: React.FC<ThemeEditorProps> = ({
    theme,
    sectionVisibility,
    navigation,
    onThemeChange,
    onSectionVisibilityChange,
    onNavigationChange,
    onUpdateField
}) => {
    const [activeTab, setActiveTab] = useState<'colors' | 'fonts' | 'sections' | 'nav'>('colors');
    const [expandedSection, setExpandedSection] = useState<string | null>('colors');

    const updateColor = (key: keyof SchoolTheme['colors'], value: string) => {
        const newTheme = {
            ...theme,
            colors: { ...theme.colors, [key]: value }
        };
        onThemeChange(newTheme);
        
        // Also update via onUpdateField for persistence
        if (onUpdateField) {
            onUpdateField(`theme.colors.${key}`, value);
            // Also update legacy colors field for backward compatibility
            if (key === 'primary' || key === 'secondary' || key === 'accent') {
                onUpdateField(`colors.${key}`, value);
            }
        }
    };

    const updateFont = (key: keyof SchoolTheme['fonts'], value: string) => {
        const newTheme = {
            ...theme,
            fonts: { ...theme.fonts, [key]: value }
        };
        onThemeChange(newTheme);
        if (onUpdateField) {
            onUpdateField(`theme.fonts.${key}`, value);
        }
    };

    const toggleSection = (key: keyof SectionVisibility) => {
        const newVisibility = {
            ...sectionVisibility,
            [key]: !sectionVisibility[key]
        };
        onSectionVisibilityChange(newVisibility);
        if (onUpdateField) {
            onUpdateField(`sectionVisibility.${key}`, !sectionVisibility[key]);
        }
    };

    const updateNavItem = (index: number, field: keyof NavItem, value: any) => {
        const newNav = [...navigation];
        newNav[index] = { ...newNav[index], [field]: value };
        onNavigationChange(newNav);
        if (onUpdateField) {
            onUpdateField(`nav`, newNav);
        }
    };

    const addNavItem = () => {
        const newNav = [
            ...navigation,
            { 
                id: `nav-${Date.now()}`, 
                label: 'New Link', 
                href: '#', 
                visible: true 
            }
        ];
        onNavigationChange(newNav);
        if (onUpdateField) {
            onUpdateField(`nav`, newNav);
        }
    };

    const removeNavItem = (index: number) => {
        const newNav = navigation.filter((_, i) => i !== index);
        onNavigationChange(newNav);
        if (onUpdateField) {
            onUpdateField(`nav`, newNav);
        }
    };

    const applyColorPreset = (preset: typeof COLOR_PRESETS[number]) => {
        const newTheme = {
            ...theme,
            colors: {
                ...theme.colors,
                primary: preset.primary,
                secondary: preset.secondary,
                accent: preset.accent
            }
        };
        onThemeChange(newTheme);
        if (onUpdateField) {
            onUpdateField('theme.colors', newTheme.colors);
            onUpdateField('colors', { primary: preset.primary, secondary: preset.secondary, accent: preset.accent });
        }
    };

    const resetToDefaults = () => {
        onThemeChange(DEFAULT_THEME);
        onSectionVisibilityChange(DEFAULT_SECTION_VISIBILITY);
        if (onUpdateField) {
            onUpdateField('theme', DEFAULT_THEME);
            onUpdateField('sectionVisibility', DEFAULT_SECTION_VISIBILITY);
        }
    };

    const sectionLabels: Record<keyof SectionVisibility, string> = {
        hero: 'Hero Banner',
        about: 'About Section',
        features: 'Why Choose Us',
        programs: 'Programs/Academics',
        team: 'Leadership Team',
        facilities: 'Facilities',
        gallery: 'Photo Gallery',
        news: 'News & Events',
        testimonials: 'Testimonials',
        downloads: 'Downloads',
        contact: 'Contact Section',
        cta: 'Call to Action',
        stats: 'Statistics'
    };

    return (
        <div className="bg-slate-900 text-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                    <Palette size={20} /> Theme Customization
                </h2>
                <p className="text-xs text-white/70 mt-1">Customize your website's look and feel</p>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-slate-700">
                {[
                    { id: 'colors', label: 'Colors', icon: Palette },
                    { id: 'fonts', label: 'Fonts', icon: Type },
                    { id: 'sections', label: 'Sections', icon: LayoutGrid },
                    { id: 'nav', label: 'Navigation', icon: Navigation }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={cn(
                            "flex-1 py-3 px-2 text-xs font-medium flex flex-col items-center gap-1 transition-all",
                            activeTab === tab.id 
                                ? "bg-slate-800 text-purple-400 border-b-2 border-purple-400" 
                                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                        )}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-[60vh] overflow-y-auto">
                {/* Colors Tab */}
                {activeTab === 'colors' && (
                    <div className="space-y-6">
                        {/* Color Presets */}
                        <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-3 block">Quick Presets</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {COLOR_PRESETS.map((preset) => (
                                    <button
                                        key={preset.name}
                                        onClick={() => applyColorPreset(preset)}
                                        className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-all text-left group"
                                    >
                                        <div className="flex gap-1 mb-1">
                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                                        </div>
                                        <span className="text-xs font-medium">{preset.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Custom Colors */}
                        <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-3 block">Custom Colors</Label>
                            <div className="space-y-3">
                                {[
                                    { key: 'primary', label: 'Primary (Header/Nav)' },
                                    { key: 'secondary', label: 'Secondary (Buttons/CTA)' },
                                    { key: 'accent', label: 'Accent (Highlights)' },
                                    { key: 'background', label: 'Background' },
                                    { key: 'text', label: 'Text Color' },
                                    { key: 'textMuted', label: 'Muted Text' }
                                ].map((color) => (
                                    <div key={color.key} className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={theme.colors[color.key as keyof typeof theme.colors]}
                                            onChange={(e) => updateColor(color.key as keyof typeof theme.colors, e.target.value)}
                                            className="w-10 h-10 rounded-lg cursor-pointer border-2 border-slate-600 bg-transparent"
                                        />
                                        <div className="flex-1">
                                            <Label className="text-sm">{color.label}</Label>
                                            <Input
                                                value={theme.colors[color.key as keyof typeof theme.colors]}
                                                onChange={(e) => updateColor(color.key as keyof typeof theme.colors, e.target.value)}
                                                className="h-7 text-xs bg-slate-800 border-slate-600 mt-1"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Fonts Tab */}
                {activeTab === 'fonts' && (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-3 block">Heading Font</Label>
                            <div className="grid grid-cols-1 gap-2">
                                {FONT_OPTIONS.slice(0, 10).map((font) => (
                                    <button
                                        key={font.name}
                                        onClick={() => updateFont('heading', font.name)}
                                        className={cn(
                                            "p-3 rounded-lg text-left transition-all flex items-center justify-between",
                                            theme.fonts.heading === font.name 
                                                ? "bg-purple-600/30 border border-purple-500" 
                                                : "bg-slate-800 hover:bg-slate-700"
                                        )}
                                        style={{ fontFamily: font.name }}
                                    >
                                        <div>
                                            <span className="text-sm font-medium">{font.name}</span>
                                            <span className="text-xs text-slate-400 ml-2">({font.category})</span>
                                        </div>
                                        {theme.fonts.heading === font.name && <Check size={16} className="text-purple-400" />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs text-slate-400 uppercase tracking-wider mb-3 block">Body Font</Label>
                            <div className="grid grid-cols-1 gap-2">
                                {FONT_OPTIONS.slice(0, 8).map((font) => (
                                    <button
                                        key={font.name}
                                        onClick={() => updateFont('body', font.name)}
                                        className={cn(
                                            "p-3 rounded-lg text-left transition-all flex items-center justify-between",
                                            theme.fonts.body === font.name 
                                                ? "bg-purple-600/30 border border-purple-500" 
                                                : "bg-slate-800 hover:bg-slate-700"
                                        )}
                                        style={{ fontFamily: font.name }}
                                    >
                                        <div>
                                            <span className="text-sm font-medium">{font.name}</span>
                                            <span className="text-xs text-slate-400 ml-2">({font.category})</span>
                                        </div>
                                        {theme.fonts.body === font.name && <Check size={16} className="text-purple-400" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Sections Tab */}
                {activeTab === 'sections' && (
                    <div className="space-y-2">
                        <p className="text-xs text-slate-400 mb-4">Toggle sections on or off to customize your website layout.</p>
                        {(Object.keys(sectionLabels) as (keyof SectionVisibility)[]).map((key) => (
                            <div 
                                key={key}
                                className={cn(
                                    "flex items-center justify-between p-3 rounded-lg transition-all",
                                    sectionVisibility[key] ? "bg-slate-800" : "bg-slate-800/50 opacity-60"
                                )}
                            >
                                <div className="flex items-center gap-3">
                                    {sectionVisibility[key] ? (
                                        <Eye size={16} className="text-green-400" />
                                    ) : (
                                        <EyeOff size={16} className="text-slate-500" />
                                    )}
                                    <span className="text-sm">{sectionLabels[key]}</span>
                                </div>
                                <Switch
                                    checked={sectionVisibility[key]}
                                    onCheckedChange={() => toggleSection(key)}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Navigation Tab */}
                {activeTab === 'nav' && (
                    <div className="space-y-4">
                        <p className="text-xs text-slate-400 mb-2">Customize your navigation menu items.</p>
                        
                        <div className="space-y-2">
                            {navigation.map((item, index) => (
                                <div 
                                    key={item.id || index}
                                    className={cn(
                                        "p-3 rounded-lg bg-slate-800 space-y-2",
                                        !item.visible && "opacity-60"
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <GripVertical size={14} className="text-slate-500 cursor-grab" />
                                        <Input
                                            value={item.label}
                                            onChange={(e) => updateNavItem(index, 'label', e.target.value)}
                                            className="flex-1 h-8 text-sm bg-slate-700 border-slate-600"
                                            placeholder="Label"
                                        />
                                        <Switch
                                            checked={item.visible}
                                            onCheckedChange={(checked) => updateNavItem(index, 'visible', checked)}
                                        />
                                        <button
                                            onClick={() => removeNavItem(index)}
                                            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <Input
                                        value={item.href}
                                        onChange={(e) => updateNavItem(index, 'href', e.target.value)}
                                        className="h-7 text-xs bg-slate-700 border-slate-600"
                                        placeholder="Link (e.g., #about or https://...)"
                                    />
                                </div>
                            ))}
                        </div>
                        
                        <Button
                            onClick={addNavItem}
                            variant="outline"
                            size="sm"
                            className="w-full border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-purple-500"
                        >
                            <Plus size={14} className="mr-2" /> Add Navigation Item
                        </Button>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetToDefaults}
                    className="text-slate-400 hover:text-white"
                >
                    <Undo2 size={14} className="mr-2" /> Reset Defaults
                </Button>
            </div>
        </div>
    );
};

export default ThemeEditor;
